/* eslint-disable jsx-a11y/anchor-is-valid */
import { Box, Divider, Fab, Rating } from '@mui/material';
import { FsDialog, FsDialogHeader } from '../dialog';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { useEffect, useState } from 'react';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import StarOutlinedIcon from '@mui/icons-material/StarOutlined';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import { FormikProvider, useFormik } from 'formik';
import { FormHelper } from '../../../helpers/formHelper';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import {
	FeedbackBugReportSchema,
	FeedbackSuggestionSchema,
} from '../../validations/schema';
import { InputWrapper } from '../formUtilities/useInput';
import { TextWrapper } from '../formUtilities/useTextWrapper';
import { FsButton } from '../button';
import { Toaster } from '../common/toaster';
import SuccessImg from '../../../assets/images/success.png';
import { FeedbackService } from '../../../services/feedbackService';
import { LanguageService } from '../../../services/languageService';
import { useTranslation } from 'react-i18next';

const Templates = {
	Main: 1,
	Suggestion: 2,
	BugReport: 3,
	RateUs: 4,
	Feedback: 5,
};

const FeedbackTypes = {
	RateUs: 1,
	ReportIssue: 2,
	MakeSuggestion: 3,
};

export const UserFeedback = () => {
	const feedbackService = new FeedbackService();
	const languageService = new LanguageService();

	const [openMenu, setOpenMenu] = useState(false);
	const [activeTemplate, setActiveTemplate] = useState(Templates.Main);
	const [loading, setLoading] = useState(false);
	const [file, setFile] = useState(null);
	const [rating, setRating] = useState(null);
	const { t } = useTranslation();
	const defalutHeading = t('howCanWeHelpYou');
	const [heading, setHeading] = useState(defalutHeading);

	const suggestionForm = useFormik({
		initialValues: {
			whatAreMissing: '',
			whatProblemWouldThatSolve: '',
			attachment: '',
		},
		validationSchema: FeedbackSuggestionSchema,
		onSubmit: () => {
			let formData = new FormData();
			formData.append('type', FeedbackTypes.MakeSuggestion);
			formData.append(
				'suggestion.whatAreMissing',
				suggestionForm.values.whatAreMissing
			);
			formData.append(
				'suggestion.whatProblemWouldThatSolve',
				suggestionForm.values.whatProblemWouldThatSolve
			);
			if (file && file?.name) {
				formData.append('suggestion.attachment', file, file.name);
			}
			onSubmitFeedback(formData);
		},
		...FormHelper.formikProps(),
	});

	const bugReportForm = useFormik({
		initialValues: {
			moreAboutTheIssue: '',
			attachment: '',
		},
		validationSchema: FeedbackBugReportSchema,
		onSubmit: () => {
			let formData = new FormData();
			formData.append('type', FeedbackTypes.ReportIssue);
			formData.append(
				'issue.moreAboutTheIssue',
				bugReportForm.values.moreAboutTheIssue
			);
			if (file && file?.name) {
				formData.append('issue.attachment', file, file.name);
			}
			onSubmitFeedback(formData);
		},
		...FormHelper.formikProps(),
	});

	const isAr = () => {
		return languageService.isArabic();
	};

	useEffect(() => {
		if (!openMenu) {
			clearAll();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [openMenu]);

	const clearAll = () => {
		setHeading(defalutHeading);
		setActiveTemplate(Templates.Main);
		suggestionForm.resetForm();
		bugReportForm.resetForm();
		setFile(null);
		setRating(null);
	};

	useEffect(() => {
		let heading = defalutHeading;
		if (activeTemplate === Templates.Suggestion) {
			heading = t('anySuggestions');
		} else if (activeTemplate === Templates.BugReport) {
			heading = t('reportBug');
		} else if (activeTemplate === Templates.RateUs) {
			heading = t('rateUs');
		}
		setHeading(heading);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTemplate]);

	const onSubmitRating = () => {
		let formData = new FormData();
		formData.append('type', FeedbackTypes.RateUs);
		formData.append('rateUs.rate', rating);
		onSubmitFeedback(formData);
	};

	const onSubmitFeedback = (formValue) => {
		setLoading(true);
		feedbackService
			.submitFeedback(formValue)
			.then((resp) => {
				setLoading(false);
				setActiveTemplate(Templates.Feedback);
				setTimeout(() => {
					setOpenMenu(false);
				}, 3000);
			})
			.catch((err) => {
				setLoading(false);
			});
	};

	const template = () => {
		switch (activeTemplate) {
			case Templates.Main: {
				return mainTemplate();
			}
			case Templates.Suggestion: {
				return suggestionTemplate();
			}
			case Templates.BugReport: {
				return bugReportTemplate();
			}
			case Templates.RateUs: {
				return rateUsTemplate();
			}
			default: {
				return mainTemplate();
			}
		}
	};

	const onFileUpload = (file) => {
		const sizeProp = getFileSize(file);
		if (sizeProp.key === 'mb' && sizeProp.value >= 10) {
			Toaster({ msg: 'File size should be less than 10 MB' });
		} else {
			setFile(file);
		}
	};

	const getFileSize = (fileFromUpload = null) => {
		let tempFile = fileFromUpload ? fileFromUpload : file;
		let kb = tempFile?.size / 1024;
		if (kb >= 1024) {
			return {
				label: `${(kb / 1024).toFixed(2)} MB`,
				key: 'mb',
				value: kb / 1024,
			};
		}
		return {
			label: `${kb.toFixed(2)} KB`,
			key: 'kb',
			value: kb,
		};
	};

	const fileAttachTemplate = () => {
		return (
			<Box>
				<input
					onChange={(e) => {
						onFileUpload(e.target.files[0]);
					}}
					hidden
					id="input-file"
					type="file"
				/>
				<TextWrapper
					type="caption"
					text={t('attachment')}></TextWrapper>
				{file ? (
					<Box className="my-2 feedback-upload-file border border-solid border-gray-400 px-2 py-1 flex justify-between items-center">
						<TextWrapper
							classList="!text-[13px] !w-[230px] !text-ellipsis !overflow-hidden !text-black"
							text={file?.name}></TextWrapper>
						<Box className="flex items-center">
							<TextWrapper
								classList="!text-[12px]"
								text={getFileSize().label}></TextWrapper>
							<Box className="feedback-remove-file">
								<CloseOutlinedIcon
									onClick={() => {
										setFile(null);
									}}
									className="!text-[13px] mx-1 cursor-pointer"
								/>
							</Box>
						</Box>
					</Box>
				) : null}
				<label
					for="input-file"
					className="border flex justify-between cursor-pointer items-center border-dashed rounded-sm border-gray-500 px-2 py-1 mt-1">
					<TextWrapper
						classList="!text-[12px]"
						text={t('clickHereToUploadFile')}></TextWrapper>
					<Box className="flex items-center">
						<InsertPhotoOutlinedIcon className="!mx-1 hover:opacity-60"></InsertPhotoOutlinedIcon>
						<SlideshowOutlinedIcon className="hover:opacity-60"></SlideshowOutlinedIcon>
					</Box>
				</label>
			</Box>
		);
	};

	const suggestionTemplate = () => {
		return (
			<FormikProvider value={suggestionForm}>
				<Box className="grid grid-flow-row gap-4 p-6">
					<InputWrapper
						label={t('whatAreYouMissing')}
						key={'whatAreMissing'}
						name="whatAreMissing"
					/>
					<InputWrapper
						isTextArea
						rows={4}
						label={t('whatProblemWouldThatSolveForYou')}
						key={'whatProblemWouldThatSolve'}
						name="whatProblemWouldThatSolve"
					/>
					{fileAttachTemplate()}
					<Box className="flex justify-center">
						<FsButton
							loading={loading}
							onClick={() => {
								suggestionForm.submitForm();
							}}>{t('submit')}</FsButton>
					</Box>
				</Box>
			</FormikProvider>
		);
	};

	const bugReportTemplate = () => {
		return (
			<FormikProvider value={bugReportForm}>
				<Box className="grid grid-flow-row gap-4 p-6">
					<InputWrapper
						isTextArea
						rows={4}
						label={t('tellUsMoreAbout')}
						key={'moreAboutTheIssue'}
						name="moreAboutTheIssue"
					/>
					{fileAttachTemplate()}
					<Box className="flex justify-center">
						<FsButton
							loading={loading}
							onClick={() => {
								bugReportForm.submitForm();
							}}>{t('submit')}</FsButton>
					</Box>
				</Box>
			</FormikProvider>
		);
	};

	const rateUsTemplate = () => {
		return (
			<Box className="p-6 flex flex-col justify-center items-center">
				<Box className="text-center mb-4">{t('rateYourExperience')}</Box>
				<Rating
					className="mb-6 !text-[42px]"
					size="large"
					name="rating"
					value={rating}
					onChange={(event, newValue) => {
						setRating(newValue);
					}}
				/>
				<FsButton
					loading={loading}
					onClick={() => {
						onSubmitRating();
					}}>{t('submit')}</FsButton>
			</Box>
		);
	};

	const mainTemplate = () => {
		return (
			<Box>
				<Box
					onClick={() => {
						setActiveTemplate(Templates.RateUs);
					}}
					className="flex items-center p-4 cursor-pointer hover:bg-gray-100">
					<Box className="mx-3 text-center">
						<StarOutlinedIcon className="!text-[38px] rating-star" />
					</Box>
					<Box className="px-4">
						<Box className="text-md font-semibold">{t('rateUs')}</Box>
						<Box className="text-sm">{t('tellUsYourExperience')}</Box>
					</Box>
				</Box>
				<Divider />
				<Box
					onClick={() => {
						setActiveTemplate(Templates.BugReport);
					}}
					className="flex items-center p-4 cursor-pointer hover:bg-gray-100">
					<Box className="mx-3 text-center">
						<BugReportOutlinedIcon className="!text-[38px] !text-red-500" />
					</Box>
					<Box className="px-4">
						<Box className="text-md font-semibold">{t('haveAnIssue')}</Box>
						<Box className="text-sm">{t('havingTroublePleaseLetUsKnow')}</Box>
					</Box>
				</Box>
				<Divider />
				<Box
					onClick={() => {
						setActiveTemplate(Templates.Suggestion);
					}}
					className="flex items-center p-4 mb-4 cursor-pointer hover:bg-gray-100">
					<Box className="mx-3 text-center">
						<MessageOutlinedIcon
							color="primary"
							className="!text-[38px]"
						/>
					</Box>
					<Box className="px-4">
						<Box className="text-md font-semibold">{t('anySuggestions')}</Box>
						<Box className="text-sm">
							{t('letUsknowwhatwecoulddotoImprove')}
						</Box>
					</Box>
				</Box>
			</Box>
		);
	};

	return (
		<Box className="relative">
			<Box
				className={`fixed bottom-[20px]  ${
					isAr() ? 'left-[20px] right-auto' : 'right-[20px] left-auto'
				}`}>
				<Fab
					size="medium"
					className={`feedback-btn ${openMenu ? 'hide-fab' : ''}`}
					onClick={() => {
						setOpenMenu(true);
					}}
					title={t('howCanWeHelpYou')}
					color="primary"
					aria-label="question">
					<QuestionMarkIcon className="!text-white" />
				</Fab>
			</Box>
			{openMenu ? (
				<FsDialog
					state={openMenu}
					fullWidth={false}
					onClose={() => {
						setOpenMenu(false);
					}}>
					<Box
						className={`${
							activeTemplate !== Templates.Feedback
								? 'md:w-[400px] w-full'
								: 'md:w-[300px] w-full'
						}`}>
						<FsDialogHeader
							title={activeTemplate !== Templates.Feedback ? heading : ''}
							onClose={() => {
								setOpenMenu(false);
							}}
						/>
						{activeTemplate !== Templates.Feedback ? (
							template()
						) : (
							<Box className="p-4 flex flex-col justify-center items-center h-[300px]">
								<img
									className="mx-3 mb-2"
									src={SuccessImg}
									alt="successImage"
								/>
								<Box className="text-center my-2 text-[24px] font-semibold">
									{t('thankYou')}
								</Box>
								<Box className="text-center text-[16px] font-[500]">
									{t('weReallyAppreciatedYourFeedback')}
								</Box>
							</Box>
						)}
					</Box>
				</FsDialog>
			) : null}
		</Box>
	);
};
